/**
 * @architecture-header 1
 * @layer module
 * @domain reports
 * @responsibility Declare the comment prompt defaults and build run and report comments.
 * @state-owner dataset store holds saved prompt settings
 * @allowed-imports shared view types, demo constants and i18n
 * @forbidden React; network; component markup
 * @public-api reports module
 * @side-effects none
 * @tests workspace Playwright flows in frontend/apps/web/e2e/workspace.spec.ts
 */

import { QUESTIONS } from '../../../shared/lib/demo-data';
import type { Translator } from '../../../shared/lib/i18n';
import type { AnswerMark } from '../../../shared/lib/model';
import { scoreAnswers } from '../../../shared/lib/selectors';
import type { PromptSettings } from '../../../shared/model/dataset-store';

export type PromptKind = 'run' | 'rep';

export const PROMPT_DEFAULT: Readonly<Record<PromptKind, string>> = {
  run: '你是這位學生的數學老師。根據他這次活動的作答判定，寫一段給家長看的課後評語。\n先講他這次做到什麼，再講卡在哪個概念，最後給一個具體的練習建議。\n不要列點，不要客套話，用第三人稱寫。',
  rep: '你是這位學生的數學老師。根據他近 30 天的自己答對比率、進步最多與最需要加強的概念，寫一段月度學習報告評語。\n先講趨勢，再講最需要處理的概念與具體證據，最後給下一步建議。\n不要列點，不要客套話，用第三人稱寫。',
};

export const PROMPT_VARIABLES: Readonly<
  Record<PromptKind, readonly (readonly [string, string])[]>
> = {
  run: [
    ['學生', '學生姓名'],
    ['分數', '答對題數 / 總題數'],
    ['訂正數', '訂正後答對的題數'],
    ['錯誤概念', '判定為答錯的概念清單'],
    ['活動', '活動名稱'],
  ],
  rep: [
    ['學生', '學生姓名'],
    ['起始比率', '六週前的自己答對比率'],
    ['目前比率', '本週的自己答對比率'],
    ['進步概念', '進步最多的概念'],
    ['加強概念', '最需要加強的概念'],
    ['證據', '完成題數與提示次數'],
  ],
};

export const PROMPT_TONES: readonly (readonly [string, string])[] = [
  ['warm', '鼓勵'],
  ['neutral', '中性'],
  ['strict', '嚴格'],
];

export const PROMPT_LENGTHS: readonly (readonly [string, string])[] = [
  ['short', '簡短'],
  ['mid', '標準'],
  ['long', '詳細'],
];

export interface ResolvedPrompt {
  readonly text: string;
  readonly tone: 'warm' | 'neutral' | 'strict';
  readonly length: 'short' | 'mid' | 'long';
  readonly customised: boolean;
}

export function resolvePrompt(kind: PromptKind, settings: PromptSettings): ResolvedPrompt {
  return {
    text: settings.text ?? PROMPT_DEFAULT[kind],
    tone: settings.tone ?? 'neutral',
    length: settings.length ?? 'mid',
    customised: settings.text != null || Boolean(settings.tone) || Boolean(settings.length),
  };
}

function toneHeading(tone: ResolvedPrompt['tone'], t: Translator): string {
  if (tone === 'warm') return t('語氣鼓勵');
  if (tone === 'strict') return t('語氣嚴格');
  return '';
}

export function buildRunComment(
  student: string,
  marks: readonly AnswerMark[],
  prompt: ResolvedPrompt,
  t: Translator,
): string {
  const score = scoreAnswers(marks);
  const wrongConcepts = marks
    .map((mark, index) => (mark === 'no' ? QUESTIONS[index].concept : null))
    .filter((concept): concept is string => Boolean(concept));
  const unique = [...new Set(wrongConcepts)];
  const core =
    student +
    t('評語前綴') +
    `${score.correct} / ${score.total}` +
    t('評語中段') +
    (score.fixed ? `${score.fixed}${t('題訂正後答對')}。` : '') +
    (unique.length
      ? unique.map((concept) => t(concept)).join('、') + t('評語結尾')
      : t('評語全對'));
  if (prompt.length === 'short') {
    return (
      student +
      t('評語前綴') +
      `${score.correct} / ${score.total}。` +
      (unique.length ? unique.map((concept) => t(concept)).join('、') + t('評語結尾') : '')
    );
  }
  const heading = toneHeading(prompt.tone, t);
  return (
    (heading ? `${heading}：` : '') + core + (prompt.length === 'long' ? t('評語詳細補充') : '')
  );
}

export function buildReportComment(student: string, prompt: ResolvedPrompt, t: Translator): string {
  const base = student + t('評語範本');
  if (prompt.length === 'short') return student + t('報告簡短版');
  if (prompt.length === 'long') return base + t('報告詳細補充');
  return base;
}
